
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { TokenRingMixin } from '@/mixins';
import TokensSVG from '@/assets/TokensSVG.vue';
import TokenUsageRing from '@/components/TokenUsageRing/TokenUsageRing.vue';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { EnterpriseTokenUsage } from '@run-diffusion/shared';
import { EnterpriseRing, TOKEN_STATUS_HERO_COLOR, TokenStatus } from '@/utils/tokenBuckets';
import RunnitPlanTokensUsageMenuOrBottomSheet from './RunnitSideNav/RunnitPlanTokensUsageMenuOrBottomSheet.vue';

/**
 * A token amount, in one of two modes.
 *
 * Plain: the `tokens` prop, formatted (a tool's cost, a balance on a dashboard card). No interaction.
 *
 * Ring (`showRing`): the ACCOUNT ROW. The number, its status colour and the consumption ring for the
 * active account (or for a pinned team), and the click that opens the tokens dialog, which this
 * component also owns. It is the sidebar account card's design, and it is the one row every surface
 * renders: the card itself, the chip beside the Run button, and each row of the company teams table.
 */
export default Vue.extend({
	name: 'RunnitTokens',
	mixins: [
		TokenRingMixin,
	],
	props: {
		loading: { type: Boolean, default: false },
		shorthand: { type: Boolean, default: false },
		background: { type: String, default: 'rgba(0, 0, 0, 0.6)' },
		tokenIconSize: { type: Number, default: 12 },
		tokenFontSize: { type: Number, default: 13 },
		tokens: { type: Number, default: null },
		isClubTokens: { type: Boolean, default: false }, // If true, go off of the account's own balance. If false, go off of prop `tokens`.
		menuId: { type: String, default: 'default' }, // Unique ID for this menu instance
		// Opt-in: the account row (number, colour, ring, and the click into the dialog) instead of a
		// plain amount. Only meaningful for an account's own balance (isClubTokens): the active
		// account, or a pinned team (below). A ring means nothing beside a cost in a dialog.
		showRing: { type: Boolean, default: false },
		/*
		Ring mode's density. 'row' is the sidebar account card's full-width band (its 24px icon column
		lines the number up with the account name above it, and the ring sits at the far edge); 'chip'
		is the same row compacted into an inline pill for the space beside the Run button and the
		company teams table, where the band's height and air would dwarf everything around it.
		 */
		variant: {
			type: String,
			default: 'chip',
			validator: (value: string): boolean => ['chip', 'row'].includes(value),
		},
		/*
		Show THIS team instead of the active account, ring and all, and open the dialog for it. The
		company teams table hands each row its team and balance doc. Only enterprise teams are ever
		pinned: the dialog's consumer layout reads the active account from the store, so it cannot
		describe another team's.
		 */
		contextTeam: { type: Object, default: null },
		contextBalanceAccount: { type: Object, default: null },
	},
	computed: {
		// team, runnitState and the balance docs' loading flags come from the mixins
		...mapState([
			'upsellDialog',
		]),
		...mapGetters([
			'displayLabels',
		]),
		/*
		What the ring row shows. A pinned team's figures come from TokenRingMixin's methods, called
		with that team; the active account's from its computeds, which already know which of the
		three account shapes it is.
		 */
		pinnedRing (): EnterpriseRing {
			return this.contextTeam ? this.enterpriseRing(this.contextTeam, this.contextBalanceAccount) : null;
		},
		shownNumberDisplay (): string {
			if (this.contextTeam) return this.enterpriseUsedDisplay(this.contextTeam, this.contextBalanceAccount);
			return this.tokenNumberDisplay;
		},
		shownNumberSuffix (): string {
			return this.contextTeam ? 'used' : this.tokenNumberSuffix;
		},
		shownNumberColor (): string {
			if (this.pinnedRing) return TOKEN_STATUS_HERO_COLOR[this.pinnedRing.status] || TOKEN_STATUS_HERO_COLOR.ok;
			return this.numberColor;
		},
		shownRingFill (): number {
			return this.pinnedRing ? this.pinnedRing.fill : this.ringFill;
		},
		shownRingStatus (): TokenStatus {
			return this.pinnedRing ? this.pinnedRing.status : this.ringStatus;
		},
		shownIsDeactivatedTeam (): boolean {
			return this.contextTeam ? !this.contextTeam.isActive : this.isDeactivatedTeam;
		},
		shownIsEnterprise (): boolean {
			return this.contextTeam ? this.isEnterpriseTeam(this.contextTeam) : this.isEnterprise;
		},
		/*
		What the row says beyond the number, in the dialog's own pill treatment so the two name the
		same states the same way. The two kinds do different jobs: an enterprise viewer is told the
		state (they cannot buy their way out of it, an administrator settles it), while a consumer is
		offered the action, since the dialog this row opens is where tokens are bought. They never
		collide: an account is one or the other. Wording stays short here, where the dialog has room
		to spell it out.
		 */
		statusNote (): { text: string, kind: string } | null {
			if (!this.showRing || this.showRingSkeleton || this.shownIsDeactivatedTeam) return null;
			if (this.shownIsEnterprise) {
				const usage: EnterpriseTokenUsage = this.contextTeam
					? this.enterpriseUsage(this.contextTeam, this.contextBalanceAccount)
					: this.enterpriseUsage(this.team, this.teamBalanceAccount);
				if (usage.isPausedOut) return { text: 'Paused', kind: 'danger' };
				if (usage.isOver) return { text: 'Over budget', kind: 'danger' };
				return null;
			}
			if (this.shownRingStatus === 'depleted' || this.shownRingStatus === 'low') {
				return { text: 'Need tokens?', kind: 'action' };
			}
			return null;
		},
		isRingRow (): boolean {
			return this.showRing && this.variant === 'row';
		},
		/*
		The ring row's placeholder. The mixin's balanceLoading is the strict test (a binding not yet
		started counts as loading), so the row never renders a fabricated "0" before the account
		lands. A pinned team's doc is already loaded; its caller shows a skeleton until then.
		 */
		showRingSkeleton (): boolean {
			if (this.loading) return true;
			return !this.contextTeam && this.balanceLoading;
		},
		isRunnitTokensContainerClickable () {
			return !!(
				this.showRing ||
				(
					this.menuId &&
					this.menuId === this._get(this.upsellDialog, 'runnitPlanDetailsData.menuId')
				)
			);
		},
		invalidTeamSelected () {
			if (this.contextTeam) return false;
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				!this.team
			);
		},
		// Plain mode's spinner; the ring row has its own skeleton (showRingSkeleton)
		isRunnitTokensLoading () {
			if (this.loading) return true;

			if (!this.isClubTokens) return false;

			if (!this.runnitState) return true;

			if (this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM) {
				return !!this.loadingTeamBalanceAccount;
			}

			return !!this.loadingUserBalanceAccount;
		},
		localTokens () {
			if (!this.isClubTokens) return this.tokens || 0;
			return (this.balanceAccountRunnitTokensData.combinedTokens || 0);
		},
	},
	methods: {
		...mapActions([
			'setUpsellDialog',
		]),
		onToggleRunnitPlanDetails () {
			if (this.invalidTeamSelected) return;
			if (this.upsellDialog.runnitPlanDetailsData?.open) {
				this.setUpsellDialog({
					runnitPlanDetailsData: {
						open: false,
						menuId: this.menuId,
					},
				});
			} else {
				this.setUpsellDialog({
					runnitPlanDetailsData: {
						open: true,
						menuId: this.menuId,
					},
				});
			}
		},
	},
	components: {
		TokensSVG,
		TokenUsageRing,
		RunnitPlanTokensUsageMenuOrBottomSheet,
	},
});
